import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { ICONS } from '@app/styles';
import { getDispositionAction } from '../../../../_utils/disposition-action.js';
import { NoteNodeType, } from './types.js';
const titleStart = '[[TITLE_START]]';
const titleEnd = '[[TITLE_END]]';
const dispositionStart = '[[DISPOSITION_START]]';
const dispositionEnd = '[[DISPOSITION_END]]';
function escapeRegExp(value) {
    return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}
const dispositionSectionPattern = new RegExp(`${escapeRegExp(dispositionStart)}[\\s\\S]*?${escapeRegExp(dispositionEnd)}`, 'g');
export function stripDispositionSections(text) {
    dispositionSectionPattern.lastIndex = 0;
    return text.replace(dispositionSectionPattern, '');
}
const markers = [
    { token: titleStart, kind: 'title-start' },
    { token: titleEnd, kind: 'title-end' },
    { token: dispositionStart, kind: 'disposition-start' },
    { token: dispositionEnd, kind: 'disposition-end' },
];
function nextMarker(text, from) {
    let best;
    for (const marker of markers) {
        const index = text.indexOf(marker.token, from);
        if (index < 0)
            continue;
        if (!best || index < best.index)
            best = { index, token: marker.token, kind: marker.kind };
    }
    return best;
}
function pushText(nodes, text) {
    if (!text.length)
        return;
    nodes.push({ type: NoteNodeType.Text, content: text });
}
function dispositionIcons(dispositionType, onRemoveDisposition) {
    if (dispositionType !== 'admit' && dispositionType !== 'discharge')
        return;
    const StampIcon = getDispositionAction(dispositionType).Icon;
    return {
        type: NoteNodeType.DispositionIcons,
        content: [
            _jsx(StampIcon.component, { className: 'live-mdm__stamp-icon', asSpan: true }, 'dispo'),
            _jsx(ICONS.x.component, { className: 'live-mdm__stamp-remove', onClick: onRemoveDisposition, asSpan: true }, 'remove')
        ],
    };
}
function makeDisposition(children, dispositionType, onRemoveDisposition) {
    const icons = dispositionIcons(dispositionType, onRemoveDisposition);
    return {
        type: NoteNodeType.Disposition,
        dispositionType,
        content: icons ? [icons, ...children] : children,
    };
}
function parseRange(text, from, stopKind, dispositionType, onRemoveDisposition) {
    const nodes = [];
    let cursor = from;
    while (cursor < text.length) {
        const marker = nextMarker(text, cursor);
        if (!marker) {
            pushText(nodes, text.slice(cursor));
            return { nodes, next: text.length, closed: stopKind === undefined };
        }
        if (marker.index > cursor)
            pushText(nodes, text.slice(cursor, marker.index));
        if (stopKind === 'disposition-end' && marker.kind === 'disposition-end')
            return { nodes, next: marker.index + marker.token.length, closed: true };
        if (marker.kind === 'title-start') {
            const end = text.indexOf(titleEnd, marker.index + titleStart.length);
            if (end < 0) {
                pushText(nodes, text.slice(marker.index));
                return { nodes, next: text.length, closed: stopKind === undefined };
            }
            nodes.push({
                type: NoteNodeType.Title,
                content: text.slice(marker.index + titleStart.length, end),
            });
            cursor = end + titleEnd.length;
            continue;
        }
        if (marker.kind === 'disposition-start' && dispositionType) {
            const inner = parseRange(text, marker.index + dispositionStart.length, 'disposition-end', dispositionType, onRemoveDisposition);
            if (!inner.closed) {
                pushText(nodes, dispositionStart);
                nodes.push(...inner.nodes);
                return { nodes, next: text.length, closed: stopKind === undefined };
            }
            nodes.push(makeDisposition(inner.nodes, dispositionType, onRemoveDisposition));
            cursor = inner.next;
            continue;
        }
        pushText(nodes, marker.token);
        cursor = marker.index + marker.token.length;
    }
    return { nodes, next: cursor, closed: stopKind === undefined };
}
export function buildNoteTree(text, dispositionType, onRemoveDisposition) {
    const { nodes } = parseRange(text, 0, undefined, dispositionType, onRemoveDisposition);
    return { type: NoteNodeType.General, content: nodes };
}
function renderTitle(content, key, leading) {
    return _jsxs("span", { className: 'title', children: [leading, content] }, key);
}
function renderDisposition(node, key) {
    const icons = node.content.find(child => child.type === NoteNodeType.DispositionIcons);
    const body = node.content.filter(child => child.type !== NoteNodeType.DispositionIcons);
    let placedIcons = false;
    return _jsxs("span", { className: 'disposition', children: [body.map((child, index) => {
                if (!placedIcons && icons && child.type === NoteNodeType.Title) {
                    placedIcons = true;
                    return renderTitle(child.content, index, icons.content);
                }
                return renderNoteNode(child, index);
            }), !placedIcons && icons?.content] }, key);
}
function renderNoteNode(node, key) {
    switch (node.type) {
        case NoteNodeType.Text:
            return node.content;
        case NoteNodeType.Title:
            return renderTitle(node.content, key);
        case NoteNodeType.DispositionIcons:
            return node.content;
        case NoteNodeType.Disposition:
            return renderDisposition(node, key);
        case NoteNodeType.General:
            return node.content.map((child, index) => renderNoteNode(child, index));
    }
}
export function renderNoteTree(node) {
    return renderNoteNode(node, 0);
}
export function formatNoteAIText(text, dispositionType, onRemoveDisposition) {
    if (!text?.length)
        return _jsx("p", { children: "No note content to present yet." });
    return _jsx("p", { children: renderNoteTree(buildNoteTree(text, dispositionType, onRemoveDisposition)) });
}
//# sourceMappingURL=utils.js.map